import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { dayjs } from '@/lib/utils'

interface DateRange {
  from: string
  to: string
}

// Generic dimension filter value (supports single or multi-select)
type FilterValue = string | string[] | number | number[] | boolean | null

interface FiltersState {
  // Date range
  dateRange: DateRange
  setDateRange: (range: DateRange) => void
  setPreset: (preset: 'today' | '7d' | '30d' | '90d' | 'mtd' | 'ytd') => void

  // Channel filter (legacy - kept for backward compatibility)
  selectedChannels: string[]
  setSelectedChannels: (channels: string[]) => void
  toggleChannel: (channel: string) => void

  // Global sidebar filters
  globalChannel: string | null
  setGlobalChannel: (id: string | null) => void
  globalWarehouse: string | null
  setGlobalWarehouse: (id: string | null) => void

  // Generic dimension filters (data model driven)
  dimensionFilters: Record<string, FilterValue>
  setDimensionFilter: (dimensionId: string, value: FilterValue) => void
  clearDimensionFilter: (dimensionId: string) => void
  clearAllDimensionFilters: () => void

  // Compare mode
  compareEnabled: boolean
  compareDateRange: DateRange | null
  setCompareEnabled: (enabled: boolean) => void
  setCompareDateRange: (range: DateRange | null) => void

  // Reset
  resetFilters: () => void
}

const getPresetDates = (preset: string): DateRange => {
  const today = dayjs()

  switch (preset) {
    case 'today':
      return {
        from: today.format('YYYY-MM-DD'),
        to: today.format('YYYY-MM-DD'),
      }
    case '7d':
      return {
        from: today.subtract(6, 'day').format('YYYY-MM-DD'),
        to: today.format('YYYY-MM-DD'),
      }
    case '30d':
      return {
        from: today.subtract(29, 'day').format('YYYY-MM-DD'),
        to: today.format('YYYY-MM-DD'),
      }
    case '90d':
      return {
        from: today.subtract(89, 'day').format('YYYY-MM-DD'),
        to: today.format('YYYY-MM-DD'),
      }
    case 'mtd':
      return {
        from: today.startOf('month').format('YYYY-MM-DD'),
        to: today.format('YYYY-MM-DD'),
      }
    case 'ytd':
      return {
        from: today.startOf('year').format('YYYY-MM-DD'),
        to: today.format('YYYY-MM-DD'),
      }
    default:
      return {
        from: today.subtract(29, 'day').format('YYYY-MM-DD'),
        to: today.format('YYYY-MM-DD'),
      }
  }
}

const defaultDateRange = getPresetDates('30d')

export const useFiltersStore = create<FiltersState>()(
  persist(
    (set) => ({
      // Date range
      dateRange: defaultDateRange,
      setDateRange: (range) => set({ dateRange: range }),
      setPreset: (preset) => set({ dateRange: getPresetDates(preset) }),

      // Global sidebar filters
      globalChannel: null,
      setGlobalChannel: (id) => set({ globalChannel: id }),
      globalWarehouse: null,
      setGlobalWarehouse: (id) => set({ globalWarehouse: id }),

      // Channels (legacy)
      selectedChannels: [],
      setSelectedChannels: (channels) => set({ selectedChannels: channels }),
      toggleChannel: (channel) =>
        set((state) => ({
          selectedChannels: state.selectedChannels.includes(channel)
            ? state.selectedChannels.filter((c) => c !== channel)
            : [...state.selectedChannels, channel],
        })),

      // Generic dimension filters
      dimensionFilters: {},
      setDimensionFilter: (dimensionId, value) =>
        set((state) => ({
          dimensionFilters: {
            ...state.dimensionFilters,
            [dimensionId]: value,
          },
        })),
      clearDimensionFilter: (dimensionId) =>
        set((state) => {
          const { [dimensionId]: _removed, ...rest } = state.dimensionFilters
          void _removed // Suppress unused variable warning
          return { dimensionFilters: rest }
        }),
      clearAllDimensionFilters: () => set({ dimensionFilters: {} }),

      // Compare
      compareEnabled: false,
      compareDateRange: null,
      setCompareEnabled: (enabled) =>
        set({
          compareEnabled: enabled,
          compareDateRange: enabled
            ? getPresetDates('30d') // Default compare range
            : null,
        }),
      setCompareDateRange: (range) => set({ compareDateRange: range }),

      // Reset
      resetFilters: () =>
        set({
          dateRange: defaultDateRange,
          globalChannel: null,
          globalWarehouse: null,
          selectedChannels: [],
          dimensionFilters: {},
          compareEnabled: false,
          compareDateRange: null,
        }),
    }),
    {
      name: 'leviathan-filters',
    }
  )
)
